/**
 * Dashboard filters — date range, comparison and chip selections — kept per
 * **filter scope**: a data source, and within the live source the saved
 * scope the page belongs to (`live:common`, `live:influencer`,
 * `live:dashboard:<id>`; see features/filters/scope.ts). So `/test` and `/`
 * never share a selection, and each custom report keeps its own.
 *
 * All values are ISO strings and plain arrays (serializable, stable cache
 * keys). A chip with every option selected is stored as *absent*, so a query
 * is unconstrained on that dimension; `[]` means "matches nothing".
 */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { DateRangeValue, DimensionKey, Selections, SourceId } from '@/data-sources/contract';
import type { IsoDate } from '@/lib/analytics/dates';
import { defaultRange, normaliseRange, previousPeriod } from '@/lib/analytics/periods';

export type FilterState = {
  range: DateRangeValue;
  /** `null` = comparison off. */
  compare: DateRangeValue | null;
  selections: Selections;
};

/** `mock`, or `live:<scope>` — the source is always the key's first segment. */
export type FilterScopeKey = string;

export type FiltersSliceState = Partial<Record<FilterScopeKey, FilterState>>;

const initialState: FiltersSliceState = {};

type ForScope<T = object> = PayloadAction<{ scope: FilterScopeKey } & T>;

function ensure(state: FiltersSliceState, scope: FilterScopeKey): FilterState {
  const current = state[scope];
  if (!current) throw new Error(`filters: "${scope}" used before initFilters`);
  return current;
}

export const filtersSlice = createSlice({
  name: 'filters',
  initialState,
  reducers: {
    /**
     * Idempotent: the first visit to a scope seeds its filters — from what the
     * person saved last time, else from the source clock's defaults.
     */
    initFilters(state, action: ForScope<{ today: IsoDate; saved?: FilterState | null }>) {
      const { scope, today, saved } = action.payload;
      if (state[scope]) return;
      if (saved) {
        const selections: Record<string, string[]> = {};
        for (const [dimension, values] of Object.entries(saved.selections)) {
          if (values) selections[dimension] = [...values];
        }
        state[scope] = {
          range: normaliseRange(saved.range),
          compare: saved.compare ? normaliseRange(saved.compare) : null,
          selections,
        };
        return;
      }
      const range = defaultRange(today);
      state[scope] = { range, compare: previousPeriod(range), selections: {} };
    },
    setRange(state, action: ForScope<{ range: DateRangeValue }>) {
      const f = ensure(state, action.payload.scope);
      f.range = normaliseRange(action.payload.range);
      // Comparison follows the range while it is on (prototype reducer).
      if (f.compare) f.compare = previousPeriod(f.range);
    },
    setCompare(state, action: ForScope<{ range: DateRangeValue | null }>) {
      const f = ensure(state, action.payload.scope);
      const r = action.payload.range;
      f.compare = r && (r.start || r.end) ? normaliseRange(r) : null;
    },
    setSelection(
      state,
      action: ForScope<{
        dimension: DimensionKey;
        values: readonly string[];
        allValues: readonly string[];
      }>,
    ) {
      const f = ensure(state, action.payload.scope);
      const { dimension, values, allValues } = action.payload;
      const all = values.length === allValues.length && allValues.every((v) => values.includes(v));
      if (all) delete f.selections[dimension];
      else f.selections[dimension] = [...values].sort();
    },
    clearSelections(state, action: ForScope) {
      ensure(state, action.payload.scope).selections = {};
    },
    /** Forget every scope of a source (a new sign-in: the old selections are another workspace's ids). */
    resetFilters(state, action: PayloadAction<{ source: SourceId }>) {
      const { source } = action.payload;
      for (const key of Object.keys(state)) {
        if (key === source || key.startsWith(`${source}:`)) delete state[key];
      }
    },
  },
});

export const { initFilters, setRange, setCompare, setSelection, clearSelections, resetFilters } =
  filtersSlice.actions;
